import { type FC, useEffect, useMemo, useRef } from "react";

// Brand-adjacent palette; raw hexes on purpose — decorative one-shot,
// no semantic token fits falling confetti.
const COLORS = ["#24727f", "#f59e0b", "#16a34a", "#dc2626", "#7c3aed", "#0ea5e9"];

// Index-seeded pseudo-random in [0, 1): pure, so renders stay stable
// (the react-hooks/purity rule bans Math.random in render, rightly).
const jitter = (i: number, salt: number): number => {
  const x = Math.sin(i * 127.1 + salt * 311.7) * 43758.5453;
  return x - Math.floor(x);
};

/** One-shot celebration: ~90 CSS-animated pieces rain over the page for
 *  a few seconds, then the component asks to be unmounted. Pure CSS —
 *  deliberately dependency-free. */
export const ConfettiBurst: FC<{ onDone: () => void }> = ({ onDone }) => {
  const pieces = useMemo(
    () =>
      Array.from({ length: 90 }, (_, i) => ({
        left: jitter(i, 1) * 100,
        delay: jitter(i, 2) * 0.6,
        duration: 2 + jitter(i, 3) * 1.6,
        size: 6 + jitter(i, 4) * 6,
        color: COLORS[i % COLORS.length],
        tilt: jitter(i, 5) > 0.5 ? 45 : -45,
      })),
    [],
  );

  // Arm the dismiss timer exactly once — call sites pass inline onDone
  // closures whose identity changes every render, and the results page
  // re-renders on every poll; depending on onDone would keep resetting
  // the timer and the burst would never settle.
  const onDoneRef = useRef(onDone);
  useEffect(() => {
    onDoneRef.current = onDone;
  });
  useEffect(() => {
    const timer = window.setTimeout(() => onDoneRef.current(), 4200);
    return () => window.clearTimeout(timer);
  }, []);

  return (
    <div className="pointer-events-none fixed inset-0 z-50 overflow-hidden" aria-hidden>
      <style>{`@keyframes mt-confetti-fall { 0% { transform: translateY(-8vh) rotate(0deg); opacity: 1; } 100% { transform: translateY(110vh) rotate(720deg); opacity: 0.7; } }`}</style>
      {pieces.map((p, i) => (
        <span
          key={i}
          style={{
            position: "absolute",
            top: "-3vh",
            left: `${p.left}%`,
            width: p.size,
            height: p.size * 0.45,
            backgroundColor: p.color,
            borderRadius: 1,
            transform: `rotate(${p.tilt}deg)`,
            animation: `mt-confetti-fall ${p.duration}s ${p.delay}s ease-in forwards`,
          }}
        />
      ))}
    </div>
  );
};
